import { useState, useEffect, useRef, useCallback } from 'react';
import { VocabWord } from '../types';
import { apiClient } from '../utils/api';
import { getRandomItem } from '../utils/helpers';
import { THEMES } from '../config/constants';

export const useVocabGame = (preloadedVocab?: VocabWord[] | null, onPreloadComplete?: () => void) => {
  const [words, setWords] = useState<VocabWord[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(30);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [nextVocab, setNextVocab] = useState<VocabWord[] | null>(null);

  // Timer interval ref
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Store callbacks in refs to avoid recreating dependent functions
  const onPreloadCompleteRef = useRef(onPreloadComplete);
  useEffect(() => {
    onPreloadCompleteRef.current = onPreloadComplete;
  }, [onPreloadComplete]);

  // Prefetch next vocabulary set
  const prefetchNextVocab = useCallback(async () => {
    try {
      const theme = getRandomItem(THEMES).name;
      const wordsData = await apiClient.getVocabulary(theme);
      setNextVocab(wordsData);
    } catch (error) {
      console.log('Prefetch failed, will load on demand:', error);
    }
  }, []);

  // Fetch words from API
  const fetchWords = useCallback(async (theme?: string) => {
    setLoading(true);
    try {
      const selectedTheme = theme || getRandomItem(THEMES).name;
      const wordsData = await apiClient.getVocabulary(selectedTheme);
      setWords(wordsData);
      setTimeLeft(30);
      setIsTimerActive(true);
      return wordsData;
    } catch (error) {
      console.error('Failed to fetch vocabulary:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize game with preloaded vocab or fetch new
  const initializeGame = useCallback(async () => {
    setLoading(true);
    try {
      if (preloadedVocab && preloadedVocab.length > 0) {
        setWords(preloadedVocab);
        setTimeLeft(30);
        setIsTimerActive(true);
        onPreloadCompleteRef.current?.();

        // Prefetch next set
        prefetchNextVocab();
      } else {
        await fetchWords();
        prefetchNextVocab();
      }
    } catch (error) {
      console.error('Failed to initialize game:', error);
    } finally {
      setLoading(false);
    }
  }, [preloadedVocab, fetchWords, prefetchNextVocab]);

  // Timer management
  useEffect(() => {
    if (isTimerActive && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(time => time - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsTimerActive(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isTimerActive, timeLeft]);

  // Move to next word
  const nextWord = useCallback(() => {
    if (currentIndex < words.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setTimeLeft(30);
      setIsTimerActive(true);
    }
  }, [currentIndex, words.length]);

  // Reset for new game with next vocab
  const startNewGame = useCallback((usePrefetched = true) => {
    setCurrentIndex(0);
    setTimeLeft(30);
    setIsTimerActive(true);

    if (usePrefetched && nextVocab && nextVocab.length > 0) {
      setWords(nextVocab);
      setNextVocab(null);
      // Prefetch next set
      prefetchNextVocab();
    } else {
      setLoading(true);
      fetchWords().then(() => {
        prefetchNextVocab();
        setLoading(false);
      }).catch(err => {
        console.error('Failed to start new game:', err);
        setLoading(false);
      });
    }
  }, [nextVocab, fetchWords, prefetchNextVocab]);

  // Reset timer
  const resetTimer = useCallback(() => {
    setTimeLeft(30);
    setIsTimerActive(true);
  }, []);

  // Pause timer
  const pauseTimer = useCallback(() => {
    setIsTimerActive(false);
  }, []);

  // Resume timer
  const resumeTimer = useCallback(() => {
    if (timeLeft > 0) {
      setIsTimerActive(true);
    }
  }, [timeLeft]);

  // Get current word
  const currentWord = words[currentIndex];

  // Check if game is complete
  const isGameComplete = currentIndex >= words.length - 1;

  // Progress percentage
  const progressPercentage = words.length > 0 ? ((currentIndex + 1) / words.length) * 100 : 0;

  return {
    // State
    words,
    currentIndex,
    currentWord,
    loading,
    timeLeft,
    isTimerActive,
    nextVocab,
    isGameComplete,
    progressPercentage,

    // Actions
    initializeGame,
    nextWord,
    startNewGame,
    resetTimer,
    pauseTimer,
    resumeTimer,

    // Utilities
    fetchWords,
    prefetchNextVocab
  };
};